// The "strongest signal" cell (DESIGN_SPEC §11.3, §12.17).
//   <StrongestSignal cell />   cell: SignalCell from listState.rowView
//     tag     severity glyph (no word), the tag title in mono ink, its first technique id (mute), +N other tags
//             (faint); technique and +N wrap under the title only when the cell is too narrow (the glyph hangs
//             in the indent, so wrapped text aligns); the tooltip lists every tag, most severe first
//     quiet   an Inter Tight note in ink-mute ("verdict at collection · 200 events so far"); busy → a breathing "reading…"
//     reason  a failure reason in faint mono, the full text in the title
//   From 900 to 1279 px notes may take two lines; under 900 px the table scrolls and nothing wraps.
import { Sev, Tooltip, type SevLevel } from "@/components/ui";
import { plural } from "@/lib/format";
import type { Tag } from "@/types";
import type { SignalCell } from "./listState";

const SEV = ["info", "low", "medium", "high", "critical"] as const;
// 900–1279 px rows are two lines: the technique drops under the title, notes may take a second line
// (class names stay literal so Tailwind sees them)
const WRAP = "min-[900px]:max-[1279px]:whitespace-normal min-[900px]:max-[1279px]:line-clamp-2";
const level = (s: number): SevLevel => Math.min(5, Math.max(1, Math.round(s) || 1)) as SevLevel;

function tagList(tags: readonly Tag[]): string {
  return [...tags]
    .sort((a, b) => b.severity - a.severity || (b.hits ?? 0) - (a.hits ?? 0))
    .map((t) => `${SEV[level(t.severity) - 1]}: ${t.title}`)
    .join("; ");
}

export function StrongestSignal({ cell }: { cell: SignalCell }) {
  if (cell.kind === "tag") {
    const { tag, tech, more, tags } = cell;
    return (
      <Tooltip lead={`${tags.length} behavior ${plural(tags.length, "tag")}`} text={tagList(tags)}>
        <span className="relative flex min-w-0 flex-wrap items-baseline gap-x-2.5 gap-y-0.5 pl-[21px] max-[899px]:flex-nowrap">
          <Sev level={level(tag.severity)} label={false} className="absolute top-1 left-0" />
          <span className="primary min-w-0 max-w-full truncate">{tag.title}</span>
          {(tech || more > 0) && (
            <span className="inline-flex shrink-0 items-baseline gap-2.5">
              {tech && (
                <span className="tech-id" translate="no">
                  {tech}
                </span>
              )}
              {more > 0 && <span className="faint">+{more}</span>}
            </span>
          )}
        </span>
      </Tooltip>
    );
  }
  if (cell.kind === "reason") {
    return (
      <span className={`block truncate text-ink-faint ${WRAP}`} title={cell.text}>
        {cell.text}
      </span>
    );
  }
  return (
    <span className={`quiet block truncate ${WRAP}`} title={cell.title ?? (cell.text || undefined)}>
      {cell.text}
      {cell.busy && (
        <>
          {cell.text && " · "}
          <span className="breathe">reading…</span>
        </>
      )}
    </span>
  );
}
